import { Suspense, lazy, useState } from 'react';
import Avatar from '@components/ui/Avatar/Avatar';
import type { AvatarSize, AvatarColor } from '@components/ui/Avatar/Avatar';
import { useTranslation } from '@hooks/useTranslation';
import { CameraIcon } from './CameraIcon';
import styles from './ProfileImageEditor.module.scss';

const CropperModal = lazy(() => import('./CropperModal'));

export interface ProfileImageEditorProps {
  /** Current image URL, or null if the entity has no image yet. */
  currentImageUrl: string | null;
  /** Display name — used for Avatar initials, modal title, and aria-labels. */
  displayName: string;
  /** Permission gate. When false, renders a static Avatar with no edit affordance. */
  canEdit: boolean;
  /**
   * Called with the cropped + compressed JPEG blob when the user clicks Save.
   * The modal awaits this Promise; rejections surface as inline errors and keep the modal open.
   */
  onSave: (blob: Blob) => Promise<void>;
  /** Optional remove handler. When provided, a "Remove photo" button appears in the modal. */
  onRemove?: () => Promise<void>;
  /** Avatar size — pass-through to <Avatar>. Default 'md'. */
  size?: AvatarSize;
  /** Avatar background color for the initials fallback. */
  color?: AvatarColor;
  /** Mobile camera capture preference for the file input. */
  captureMode?: 'user' | 'environment';
  /** Optional className on the root wrapper. */
  className?: string;
}

export default function ProfileImageEditor({
  currentImageUrl,
  displayName,
  canEdit,
  onSave,
  onRemove,
  size = 'md',
  color,
  captureMode,
  className,
}: ProfileImageEditorProps) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const avatarSrc = currentImageUrl ?? undefined;

  if (!canEdit) {
    return (
      <Avatar
        src={avatarSrc}
        name={displayName}
        size={size}
        color={color}
        className={className}
      />
    );
  }

  const overlayLabel = currentImageUrl
    ? t('K_PROFILE_IMAGE_EDIT_ARIA', 'Change profile photo')
    : t('K_PROFILE_IMAGE_ADD_ARIA', 'Add profile photo');

  // Edit affordance is unusable at xs/sm — the badge shrinks below tap-target size.
  // Render the avatar without the overlay; the consumer can mount the editor
  // elsewhere (e.g. inside a popover) at those sizes.
  const showOverlay = size !== 'xs' && size !== 'sm';

  return (
    <>
      <span className={`${styles.root} ${className ?? ''}`}>
        <Avatar src={avatarSrc} name={displayName} size={size} color={color} />
        {showOverlay && (
          <button
            type="button"
            className={styles.editOverlay}
            onClick={() => setModalOpen(true)}
            aria-label={overlayLabel}
          >
            <CameraIcon size={size === 'xl' ? 28 : size === 'lg' ? 22 : 16} />
          </button>
        )}
      </span>

      {modalOpen && (
        <Suspense fallback={null}>
          <CropperModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            displayName={displayName}
            currentImageUrl={currentImageUrl}
            onSave={onSave}
            {...(onRemove ? { onRemove } : {})}
            {...(captureMode ? { captureMode } : {})}
          />
        </Suspense>
      )}
    </>
  );
}
